import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  Portal,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { LuChevronDown, LuLogOut, LuMenu, LuUser } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { signOut } from '@/lib/auth';
import { useMe } from '@/lib/MeContext';
import { toaster } from '@/components/ui/toaster';
import { ProjectSelector } from './ProjectSelector';

const ROLE_LABEL: Record<'admin' | 'asistente', string> = {
  admin: 'Administrador',
  asistente: 'Asistente',
};

type Props = {
  onOpenNav: () => void;
};

export function TopBar({ onOpenNav }: Props) {
  const navigate = useNavigate();
  const { me, loading } = useMe();

  async function handleSignOut() {
    try {
      await signOut();
      toaster.create({ title: 'Sesión cerrada', type: 'info' });
      navigate('/login', { replace: true });
    } catch (err) {
      toaster.create({
        title: 'No se pudo cerrar sesión',
        description: err instanceof Error ? err.message : 'intenta de nuevo',
        type: 'error',
      });
    }
  }

  return (
    <Flex
      as="header"
      px={{ base: '3', md: '5' }}
      py="3"
      align="center"
      gap={{ base: '2', md: '3' }}
      borderBottomWidth="1px"
      borderColor="border"
      bg="bg.surface"
      position="sticky"
      top={0}
      zIndex={20}
    >
      <IconButton
        aria-label="Abrir menú"
        size="sm"
        variant="ghost"
        display={{ base: 'inline-flex', md: 'none' }}
        onClick={onOpenNav}
      >
        <Icon as={LuMenu} boxSize="5" />
      </IconButton>

      <ProjectSelector />

      <Box flex="1" />

      {loading ? (
        <Spinner size="sm" />
      ) : me ? (
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button size="sm" variant="outline" px={{ base: '2', md: '3' }}>
              <Icon as={LuUser} />
              <Text display={{ base: 'none', md: 'inline' }}>{me.fullName}</Text>
              <Icon as={LuChevronDown} display={{ base: 'none', md: 'inline' }} />
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content minW="240px">
                <Box px="3" py="2">
                  <VStack align="start" gap="0">
                    <Text fontSize="sm" fontWeight="600">
                      {me.fullName}
                    </Text>
                    <Text fontSize="xs" color="fg.muted">
                      {me.email}
                    </Text>
                    <HStack mt="1.5">
                      <Box
                        fontSize="2xs"
                        fontWeight="700"
                        px="1.5"
                        py="0.5"
                        rounded="sm"
                        bg="brand.50"
                        color="brand.700"
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                      >
                        {ROLE_LABEL[me.role]}
                      </Box>
                    </HStack>
                  </VStack>
                </Box>
                <Menu.Separator />
                <Menu.Item value="signout" onClick={handleSignOut}>
                  <Icon as={LuLogOut} /> Cerrar sesión
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      ) : null}
    </Flex>
  );
}
